import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { CashButton, CashField, CashNotice } from './CashPrimitives';
import { CashPeriodPicker, cashPeriodValid } from './CashPeriodPicker';
import { cashBusinessDate } from './cashBusinessDate';
import { cashCategoryLabel } from './cashCategoryText';
import { cashAmountInput, parseCashAmount } from './cashMoney';
import { cashMessage } from './cashMessages';
import { cashScopedAccounts, CashAccountOptions } from './cashAccountGroups';
const text = {
    ru: {
        title: 'Учесть расход',
        kind: 'Что учитываем',
        expense: 'Счёт / расход',
        salary: 'Зарплата',
        bonus: 'Бонус',
        month: 'Месяц расхода',
        store: 'Филиал',
        scope: 'Область начисления',
        whole: 'Весь филиал',
        additional: 'Дополнительно',
        category: 'Статья',
        recipient: 'Получатель',
        none: 'Без отдельного получателя',
        employee: 'Сотрудник',
        choose: 'Выберите',
        amount: 'Сумма',
        currency: 'Валюта',
        description: 'Название расхода',
        source: 'Основание / документ',
        reference: 'Номер основания бонуса',
        unpaid: 'За этот расход ещё не платили — прежних оплат нет',
        noncash: 'Здесь учитывается расход. Оплата — отдельным действием.',
        checking: 'Проверяем месяц и действующие права…',
        check: 'Проверить ещё раз',
        existing: 'За этот месяц уже есть начисления. Откройте нужное, чтобы не учесть расход повторно.',
        open: 'Открыть начисление',
        blocked: 'Нужны уточнение суммы, условий или проверка истории. Продолжите в существующем начислении.',
        advanced: 'Проверить историю и условия',
        save: 'Учесть расход',
        draft: 'Сохранить на подтверждение',
        draftHelp: 'Ваши права позволяют подготовить черновик. Он ещё не является подтверждённым расходом.',
        cancel: 'Отмена',
        bonusHelp: 'Для отдельного бонуса укажите собственное основание. Оклад и условия будущих месяцев не меняются.',
        required: 'Заполните основание и явно подтвердите отсутствие прежних оплат.',
        salaryAmount: 'Сумма из действующих условий',
        noTerms: 'Для этого месяца нет доступных условий зарплаты.',
    },
    az: {
        title: 'Xərci qeydə al',
        kind: 'Nə qeydə alınır',
        expense: 'Hesab / xərc',
        salary: 'Əməkhaqqı',
        bonus: 'Mükafat',
        month: 'Xərcin ayı',
        store: 'Filial',
        scope: 'Hesablanma sahəsi',
        whole: 'Bütün filial',
        additional: 'Əlavə',
        category: 'Maddə',
        recipient: 'Alan şəxs',
        none: 'Ayrıca alan şəxs yoxdur',
        employee: 'Əməkdaş',
        choose: 'Seçin',
        amount: 'Məbləğ',
        currency: 'Valyuta',
        description: 'Xərcin adı',
        source: 'Əsas / sənəd',
        reference: 'Mükafat əsasının nömrəsi',
        unpaid: 'Bu xərc üçün əvvəl ödəniş edilməyib',
        noncash: 'Burada xərc qeydə alınır. Ödəniş ayrıca edilir.',
        checking: 'Ay və cari icazələr yoxlanılır…',
        check: 'Yenidən yoxla',
        existing: 'Bu ay üçün hesablanma var. Təkrarı önləmək üçün mövcud qeydi açın.',
        open: 'Hesablanmanı aç',
        blocked: 'Məbləğ, şərtlər və ya tarixçə yoxlanmalıdır. Mövcud hesablanmada davam edin.',
        advanced: 'Tarixçə və şərtləri yoxla',
        save: 'Xərci qeydə al',
        draft: 'Təsdiqə göndər',
        draftHelp: 'İcazəniz qaralama hazırlamağa imkan verir. Bu, hələ təsdiqlənmiş xərc deyil.',
        cancel: 'Ləğv et',
        bonusHelp: 'Ayrıca mükafat üçün ayrıca əsas göstərin. Maaş və gələcək ayların şərtləri dəyişmir.',
        required: 'Əsası daxil edin və əvvəl ödəniş olmadığını təsdiqləyin.',
        salaryAmount: 'Cari şərtlərdən məbləğ',
        noTerms: 'Bu ay üçün əlçatan əməkhaqqı şərtləri yoxdur.',
    },
    en: {
        title: 'Record expense',
        kind: 'What to record',
        expense: 'Bill / expense',
        salary: 'Salary',
        bonus: 'Bonus',
        month: 'Expense month',
        store: 'Branch',
        scope: 'Accrual scope',
        whole: 'Whole branch',
        additional: 'Additional options',
        category: 'Category',
        recipient: 'Recipient',
        none: 'No separate recipient',
        employee: 'Employee',
        choose: 'Choose',
        amount: 'Amount',
        currency: 'Currency',
        description: 'Expense name',
        source: 'Source / document',
        reference: 'Bonus source reference',
        unpaid: 'Nothing has been paid for this expense before',
        noncash: 'This records an expense. Payment is a separate action.',
        checking: 'Checking the month and current authority…',
        check: 'Check again',
        existing: 'Accruals already exist for this month. Open the relevant one to avoid recording it twice.',
        open: 'Open accrual',
        blocked: 'The amount, terms or history need review. Continue with the existing accrual.',
        advanced: 'Review history and terms',
        save: 'Record expense',
        draft: 'Save for confirmation',
        draftHelp: 'Your permissions allow a draft. It is not a confirmed expense yet.',
        cancel: 'Cancel',
        bonusHelp: 'Use a distinct source reference for a separate bonus. Salary and future terms stay unchanged.',
        required: 'Enter the source and explicitly confirm no previous payments.',
        salaryAmount: 'Amount from current terms',
        noTerms: 'No accessible salary terms exist for this month.',
    },
};
export const monthExpenseText = (locale, key) => text[locale][key];
export function CashMonthExpenseIcon({ kind = 'expense', }) {
    return (_jsx("svg", { "aria-hidden": "true", focusable: "false", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", style: { flex: 'none', verticalAlign: 'middle', marginRight: 6 }, children: kind === 'salary' ? (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "12", cy: "7", r: "3" }), _jsx("path", { d: "M5 21v-3a7 7 0 0 1 14 0v3" })] })) : kind === 'bonus' ? (_jsx(_Fragment, { children: _jsx("path", { d: "m12 3 3 6 6 1-4 5 1 6-6-3-6 3 1-6-4-5 6-1 3-6Z" }) })) : (_jsxs(_Fragment, { children: [_jsx("path", { d: "M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" }), _jsx("path", { d: "M9 8h6M9 12h6" })] })) }));
}
export function CashMonthExpenseForm({ snapshot, data, client, locale, month: initialMonth, storeId: initialStore, busy, submit, onSaved, onCancel, onDirty, onReviewBasis, onAdvanced, }) {
    const t = (key) => monthExpenseText(locale, key);
    const [kind, setKind] = useState('expense'), [month, setMonth] = useState(cashPeriodValid(initialMonth)
        ? initialMonth
        : cashBusinessDate(snapshot.businessTimezone).slice(0, 7)), [store, setStore] = useState(initialStore || ''), [account, setAccount] = useState(''), [category, setCategory] = useState(''), [employee, setEmployee] = useState(''), [party, setParty] = useState(''), [currency, setCurrency] = useState(data.availableCurrencies?.length === 1 ? data.availableCurrencies[0] : ''), [title, setTitle] = useState(''), [amount, setAmount] = useState(''), [source, setSource] = useState(''), [reference, setReference] = useState(''), [unpaid, setUnpaid] = useState(false), [retry, setRetry] = useState(0), [checking, setChecking] = useState(false), [failure, setFailure] = useState('');
    const permittedAccounts = new Set(cashScopedAccounts(data.scopes, snapshot.accounts).map(a => a.id));
    const scopes = data.scopes.filter(s => s.actions.includes('PAYABLE_PREPARE') && (!s.accountId || permittedAccounts.has(s.accountId)));
    const stores = [...new Set(scopes.map(s => s.storeId))], selectedStore = store || (stores.length === 1 ? stores[0] : '');
    const scopeOptions = scopes.filter(s => s.storeId === selectedStore), scopeAccount = scopeOptions.length === 1 ? scopeOptions[0].accountId || '' : account;
    const ScopeContainer = scopeOptions.some(s => !s.accountId) ? 'details' : 'div';
    const canonicalBonusCategory = snapshot.categories.find(c => c.direction === 'EXPENSE' && c.paymentMode === 'SALARY')?.id;
    const categories = snapshot.categories.filter(c => !c.archived &&
        c.direction === 'EXPENSE' &&
        (kind === 'bonus'
            ? c.id === canonicalBonusCategory
            : kind === 'expense'
                ? ['GENERAL', 'ACCRUAL'].includes(c.paymentMode || 'GENERAL')
                : c.paymentMode === 'SALARY'));
    const categoryId = categories.some(c => c.id === category)
        ? category
        : categories.length === 1
            ? categories[0].id
            : '';
    const person = data.employees.find(e => e.id === employee && e.active);
    const scopeValid = kind !== 'expense'
        ? Boolean(person)
        : scopeOptions.some(s => (s.accountId || '') === scopeAccount);
    const unit = currency || (data.availableCurrencies?.length === 1 ? data.availableCurrencies[0] : '');
    const input = {
        kind: kind === 'salary' ? 'SALARY' : 'GENERAL',
        categoryId,
        month,
        currency: unit,
        ...(kind === 'expense'
            ? {
                storeId: selectedStore,
                accountId: scopeAccount || undefined,
                counterpartyId: party || undefined,
            }
            : { employeeId: employee }),
        ...(kind === 'bonus' ? { componentKind: 'BONUS', sourceReference: reference.trim() } : {}),
    };
    const authority = JSON.stringify([
        snapshot.actor.id,
        snapshot.actor.level,
        snapshot.actions,
        data.scopes,
        snapshot.accounts.map(a => [a.id, a.status, a.actions]),
    ]);
    const inputKey = JSON.stringify([input, authority, client.identityKey]), [read, setRead] = useState(null);
    const readiness = read?.key === inputKey ? read.value : null, request = useRef(0);
    const [anotherBill, setAnotherBill] = useState(false);
    const draftState = JSON.stringify([
        kind,
        month,
        store,
        account,
        category,
        employee,
        party,
        currency,
        title,
        amount,
        source,
        reference,
        unpaid,
        anotherBill,
    ]);
    const baseline = useRef(draftState);
    useEffect(() => {
        setUnpaid(false);
        setAnotherBill(false);
    }, [inputKey, retry]);
    useEffect(() => onDirty(draftState !== baseline.current), [draftState, onDirty]);
    useEffect(() => {
        const current = ++request.current, controller = new AbortController();
        setRead(null);
        setFailure('');
        if (!client.paymentReadiness ||
            !scopeValid ||
            !categoryId ||
            !cashPeriodValid(month) ||
            !unit ||
            (kind === 'bonus' && !reference.trim())) {
            setChecking(false);
            return () => controller.abort();
        }
        setChecking(true);
        client
            .paymentReadiness(input, controller.signal)
            .then(value => {
            if (!controller.signal.aborted && request.current === current && client.isCurrent())
                setRead({ key: inputKey, value });
        })
            .catch(cause => {
            if (!controller.signal.aborted && request.current === current && client.isCurrent()) {
                const fault = client.failure(cause);
                setFailure(cashMessage(locale, fault.message, fault.code));
            }
        })
            .finally(() => {
            if (!controller.signal.aborted && request.current === current)
                setChecking(false);
        });
        return () => {
            controller.abort();
            request.current++;
        };
    }, [inputKey, retry, client]);
    const readyAction = kind === 'bonus'
        ? 'STAFF_BONUS_READY'
        : kind === 'salary'
            ? 'SALARY_MONTH_CONFIRM'
            : 'PAYABLE_READY';
    const draftAction = kind === 'bonus' ? 'STAFF_BONUS_PREPARE' : 'PAYABLE_PREPARE';
    const canConfirm = Boolean(readiness?.actions.includes(readyAction) && readiness.readinessHash);
    const canPrepare = Boolean(readiness?.actions.includes(draftAction));
    const terms = readiness?.terms, minor = kind === 'salary' ? (terms?.amountMinor ?? null) : parseCashAmount(amount, true, unit);
    const existing = readiness?.bases || [], blocked = Boolean(readiness &&
        readiness.status !== 'MISSING_MONTH' &&
        !(kind === 'expense' && readiness.status === 'READY' && anotherBill));
    const valid = Boolean(!busy &&
        !checking &&
        scopeValid &&
        categoryId &&
        unit &&
        cashPeriodValid(month) &&
        readiness &&
        !blocked &&
        (canConfirm || canPrepare) &&
        minor !== null &&
        BigInt(minor) > 0n &&
        source.trim() &&
        unpaid &&
        (kind === 'salary' ? person && terms : title.trim()) &&
        (kind !== 'bonus' || reference.trim()));
    return (_jsxs("form", { className: "cash-management-form cash-month-expense-form", onSubmit: async (event) => {
            event.preventDefault();
            if (!valid || !readiness || minor === null)
                return;
            const selectedTitle = kind === 'salary' ? `${t('salary')} · ${person?.name} · ${month}` : title.trim();
            const payload = {
                kind: input.kind,
                title: selectedTitle,
                categoryId,
                storeId: readiness.scope?.storeId || selectedStore,
                accountId: readiness.scope?.accountId || undefined,
                employeeId: kind === 'expense' ? undefined : employee,
                counterpartyId: kind === 'expense' ? party || undefined : undefined,
                month,
                currency: kind === 'salary' ? terms.currency : unit,
                amountMinor: minor,
                source: source.trim(),
                ...(kind === 'salary'
                    ? { termsId: terms.id, expectedTerms: { id: terms.id, version: terms.version } }
                    : {}),
                ...(kind !== 'expense' ? { expectedEmployeeVersion: readiness.employee?.version } : {}),
                ...(kind === 'bonus'
                    ? { componentKind: 'BONUS', sourceReference: reference.trim() }
                    : {}),
                ...(canConfirm
                    ? {
                        expectedReadinessHash: readiness.readinessHash,
                        coverage: {
                            mode: 'ORIGINAL_WITH_PAID',
                            priorPaidMinor: '0',
                            knownZeroConfirmed: true,
                            asOf: readiness.businessDate,
                            source: source.trim(),
                        },
                    }
                    : {}),
            };
            const result = await submit(canConfirm ? readyAction : draftAction, payload);
            if (result)
                onSaved(result, canConfirm, {
                    title: selectedTitle,
                    month,
                    amountMinor: minor,
                    currency: kind === 'salary' ? terms.currency : unit,
                });
        }, children: [_jsx("style", { children: `.cash-month-expense-form .cash-fields{grid-template-columns:repeat(2,minmax(0,1fr))}.cash-month-expense-form .cash-field{min-width:0}.cash-month-expense-form input,.cash-month-expense-form select,.cash-month-expense-form textarea{font-size:16px;min-height:44px;width:100%;min-width:0}.cash-month-expense-form .cash-check input{width:auto;min-height:20px}.cash-month-expense-summary .cash-facts{font-variant-numeric:tabular-nums}@media(max-width:600px){.cash-month-expense-form .cash-fields{grid-template-columns:minmax(0,1fr)}.cash-month-expense-form .cash-footer{flex-wrap:wrap}.cash-month-prior-debts .cash-card h3{overflow-wrap:anywhere}}` }), _jsxs("p", { className: "cash-muted", children: [_jsx(CashMonthExpenseIcon, { kind: kind }), t('noncash')] }), _jsxs("fieldset", { className: "cash-command-fields", disabled: busy, children: [_jsxs("div", { className: "cash-fields", children: [_jsx(CashField, { label: t('kind'), children: _jsxs("select", { value: kind, onChange: e => {
                                        setKind(e.target.value);
                                        setCategory('');
                                        setParty('');
                                        setUnpaid(false);
                                    }, children: [_jsx("option", { value: "expense", children: t('expense') }), _jsx("option", { value: "salary", children: t('salary') }), _jsx("option", { value: "bonus", children: t('bonus') })] }) }), _jsx(CashPeriodPicker, { required: true, label: t('month'), locale: locale, value: month, onChange: value => {
                                    setMonth(value);
                                    setUnpaid(false);
                                } }), kind === 'expense' ? (_jsxs(_Fragment, { children: [_jsx(CashField, { label: t('store'), children: _jsxs("select", { value: selectedStore, onChange: e => {
                                                setStore(e.target.value);
                                                setAccount('');
                                                setParty('');
                                                setUnpaid(false);
                                            }, children: [_jsx("option", { value: "", children: t('choose') }), stores.map(id => (_jsx("option", { value: id, children: snapshot.stores.find(s => s.id === id)?.name || id }, id)))] }) }), scopeOptions.length === 1 && scopeAccount && (_jsx(CashField, { label: t('scope'), children: _jsx("input", { readOnly: true, value: snapshot.accounts.find(a => a.id === scopeAccount)?.name || scopeAccount }) })), scopeOptions.length > 1 && (_jsxs(ScopeContainer, { children: [ScopeContainer === 'details' && (_jsxs("summary", { children: [t('additional'), scopeAccount
                                                        ? ` · ${snapshot.accounts.find(a => a.id === scopeAccount)?.name || scopeAccount}`
                                                        : ''] })), _jsx(CashField, { label: t('scope'), children: _jsxs("select", { value: scopeAccount, onChange: e => {
                                                        setAccount(e.target.value);
                                                        setUnpaid(false);
                                                    }, children: [scopeOptions.some(s => !s.accountId) && (_jsx("option", { value: "", children: t('whole') })), !scopeOptions.some(s => !s.accountId) && (_jsx("option", { value: "", disabled: true, children: t('choose') })), _jsx(CashAccountOptions, { accounts: cashScopedAccounts(scopeOptions.filter(s => s.accountId), snapshot.accounts), stores: snapshot.stores, locale: locale })] }) })] })), _jsx(CashField, { label: t('recipient'), children: _jsxs("select", { value: party, onChange: e => {
                                                setParty(e.target.value);
                                                setUnpaid(false);
                                            }, children: [_jsx("option", { value: "", children: t('none') }), data.counterparties
                                                    .filter(p => p.active &&
                                                    (!p.storeId || p.storeId === selectedStore) &&
                                                    (!p.accountId || p.accountId === scopeAccount))
                                                    .map(p => (_jsx("option", { value: p.id, children: p.name }, p.id)))] }) })] })) : (_jsx(CashField, { label: t('employee'), children: _jsxs("select", { value: employee, onChange: e => {
                                        setEmployee(e.target.value);
                                        setUnpaid(false);
                                    }, children: [_jsx("option", { value: "", children: t('choose') }), data.employees
                                            .filter(e => e.active && (!initialStore || e.storeId === initialStore))
                                            .map(e => (_jsxs("option", { value: e.id, children: [e.name, !initialStore
                                                    ? ` · ${snapshot.stores.find(s => s.id === e.storeId)?.name || e.storeId}`
                                                    : ''] }, e.id)))] }) })), kind !== 'expense' && person && (_jsx(CashField, { label: `${t('store')} · ${t('scope')}`, children: _jsx("input", { readOnly: true, value: `${snapshot.stores.find(s => s.id === person.storeId)?.name || person.storeId} · ${person.accountId ? snapshot.accounts.find(a => a.id === person.accountId)?.name || person.accountId : t('whole')}` }) })), _jsx(CashField, { label: t('category'), children: _jsxs("select", { disabled: kind === 'bonus' && categories.length === 1, value: categoryId, onChange: e => {
                                        setCategory(e.target.value);
                                        setUnpaid(false);
                                    }, children: [_jsx("option", { value: "", children: t('choose') }), categories.map(c => (_jsx("option", { value: c.id, children: cashCategoryLabel(locale, c) }, c.id)))] }) }), _jsx(CashField, { label: t('currency'), children: _jsxs("select", { value: unit, onChange: e => {
                                        setCurrency(e.target.value);
                                        setUnpaid(false);
                                    }, children: [_jsx("option", { value: "", children: t('choose') }), [
                                            ...new Set([
                                                ...(data.availableCurrencies || []),
                                                ...snapshot.accounts.map(a => a.currency),
                                            ]),
                                        ].map(c => (_jsx("option", { children: c }, c)))] }) }), kind !== 'salary' && (_jsx(CashField, { label: t('description'), children: _jsx("input", { required: true, maxLength: 150, value: title, onChange: e => setTitle(e.target.value) }) })), _jsx(CashField, { label: `${kind === 'salary' ? t('salaryAmount') : t('amount')} · ${unit}`, children: _jsx("input", { required: true, inputMode: "decimal", readOnly: kind === 'salary', value: kind === 'salary'
                                        ? minor === null
                                            ? ''
                                            : cashAmountInput(minor, terms?.currency || unit)
                                        : amount, onChange: e => setAmount(e.target.value) }) }), kind === 'bonus' && (_jsxs(_Fragment, { children: [_jsx(CashNotice, { children: t('bonusHelp') }), _jsx(CashField, { label: t('reference'), children: _jsx("input", { required: true, maxLength: 150, value: reference, onChange: e => setReference(e.target.value) }) })] })), _jsx(CashField, { label: t('source'), children: _jsx("textarea", { required: true, value: source, onChange: e => setSource(e.target.value) }) }), _jsxs("label", { className: "cash-check", children: [_jsx("input", { type: "checkbox", checked: unpaid, onChange: e => setUnpaid(e.target.checked) }), t('unpaid'), readiness && ` · ${readiness.businessDate}`] })] }), checking && _jsx("p", { role: "status", children: t('checking') }), failure && (_jsxs(CashNotice, { tone: "danger", children: [failure, _jsx(CashButton, { onClick: () => setRetry(v => v + 1), children: t('check') })] })), existing.length > 0 && (_jsxs(CashNotice, { children: [t('existing'), existing.map(b => (_jsxs(CashButton, { onClick: () => onReviewBasis(b.id), children: [t('open'), " \u00B7 ", b.title, " \u00B7 ", b.month] }, b.id))), kind === 'expense' && readiness?.status === 'READY' && (_jsxs("label", { className: "cash-check", children: [_jsx("input", { type: "checkbox", checked: anotherBill, onChange: e => {
                                            setAnotherBill(e.target.checked);
                                            setUnpaid(false);
                                        } }), {
                                        ru: 'Это другой счёт — новое отдельное основание',
                                        az: 'Bu başqa hesabdır — ayrıca yeni əsas',
                                        en: 'This is a different bill — a separate new source',
                                    }[locale]] }))] })), blocked && (_jsxs(CashNotice, { tone: "warn", children: [t('blocked'), _jsx(CashButton, { onClick: onAdvanced, children: t('advanced') })] })), kind === 'salary' && readiness?.status === 'MISSING_TERMS' && (_jsx(CashNotice, { children: t('noTerms') })), !canConfirm && canPrepare && _jsx(CashNotice, { children: t('draftHelp') }), readiness && (_jsx(CashButton, { onClick: () => {
                            setUnpaid(false);
                            setRetry(v => v + 1);
                        }, children: t('check') })), _jsxs("div", { className: "cash-footer", children: [_jsx(CashButton, { onClick: onCancel, children: t('cancel') }), _jsx(CashButton, { primary: true, type: "submit", disabled: !valid, children: t(canConfirm || !canPrepare ? 'save' : 'draft') })] })] })] }));
}
